import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '@/components/feature/Logo';

// Défilement fiable vers une section de la page courante.
// Évite tout rechargement / retour en haut lié aux liens d'ancre bruts,
// et compense la hauteur réelle de la barre de navigation (fixe : 80 px).
const HEADER_OFFSET = 80;

function scrollToSection(hash: string): boolean {
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior: 'smooth' });
  return true;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    scrollToSection(href);
    window.history.replaceState(null, '', href);
  };

  const navLinks = [
    { label: 'Trouver un praticien', href: '#recherche' },
    { label: 'Comment ça marche', href: '#comment' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/">
          <Logo variant={scrolled ? 'dark' : 'light'} />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          <nav className="flex items-center gap-1">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleAnchor(e, l.href)}
                className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <span
            className={`mx-2 w-px h-6 ${scrolled ? 'bg-background-300' : 'bg-background-50/25'}`}
            aria-hidden="true"
          />

          <a
            href="#praticiens"
            onClick={(e) => handleAnchor(e, '#praticiens')}
            className={`px-3.5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'text-foreground-700 hover:text-primary-600'
                : 'text-background-50/90 hover:text-background-50'
            }`}
          >
            Espace praticien
          </a>
          <a
            href="#recherche"
            onClick={(e) => handleAnchor(e, '#recherche')}
            className="ml-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Prendre rendez-vous
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
            scrolled ? 'text-foreground-900' : 'text-background-50'
          }`}
          aria-label="Menu"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl w-6 h-6 flex items-center justify-center`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 space-y-1">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => {
                e.preventDefault();
                setMobileOpen(false);
                scrollToSection(l.href);
                window.history.replaceState(null, '', l.href);
              }}
              className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#praticiens"
            onClick={(e) => {
              e.preventDefault();
              setMobileOpen(false);
              scrollToSection('#praticiens');
              window.history.replaceState(null, '', '#praticiens');
            }}
            className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-primary-50 cursor-pointer"
          >
            Espace praticien
          </a>
          <a
            href="#recherche"
            onClick={(e) => {
              e.preventDefault();
              setMobileOpen(false);
              scrollToSection('#recherche');
              window.history.replaceState(null, '', '#recherche');
            }}
            className="block mt-2 px-3 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold text-center cursor-pointer"
          >
            Prendre rendez-vous
          </a>
        </div>
      )}
    </header>
  );
}